import { useState, useEffect, useCallback } from 'react';
import { X, CalendarClock, Save, RefreshCw } from 'lucide-react';
import { RATING_PRESETS } from './GenerationControls';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AutoGenSettings = ({ onClose }) => {
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const token = () => localStorage.getItem('admin_token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/generation-settings`, {
        headers: { 'Authorization': `Bearer ${token()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGlobalEnabled(data.global_enabled);
        setRows(data.companies || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRow = (domain, patch) => {
    setRows((prev) => prev.map((r) => (r.domain === domain ? { ...r, ...patch } : r)));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/generation-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({
          global_enabled: globalEnabled,
          companies: rows.map((r) => ({
            domain: r.domain, enabled: r.enabled,
            weekly_volume: parseInt(r.weekly_volume, 10) || 0,
            rating_preset: r.rating_preset,
          })),
        }),
      });
      if (res.ok) setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="autogen-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full p-6 my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#00b67a]" />
              Génération automatique quotidienne
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Chaque jour à 09h00 (Paris), des avis IA sont générés selon ces réglages. Aucun autre système de génération n'est nécessaire.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="autogen-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Interrupteur global */}
        <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 mb-4">
          <div>
            <p className="font-medium text-[#1a1a1a]">Génération automatique</p>
            <p className="text-xs text-gray-500">Interrupteur global. Désactivé = aucune génération quotidienne.</p>
          </div>
          <button
            onClick={() => { setGlobalEnabled((v) => !v); setSaved(false); }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${globalEnabled ? 'bg-[#00b67a]' : 'bg-gray-300'}`}
            data-testid="autogen-global-toggle"
            role="switch"
            aria-checked={globalEnabled}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${globalEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-500 text-sm">Chargement…</div>
        ) : (
          <div className={`border rounded-lg overflow-hidden ${globalEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-gray-500 border-b">
                    <th className="px-3 py-2 font-medium">Entreprise</th>
                    <th className="px-3 py-2 font-medium text-center">Active</th>
                    <th className="px-3 py-2 font-medium">Avis / semaine</th>
                    <th className="px-3 py-2 font-medium">Tendance des notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.domain} className="border-b last:border-0" data-testid={`autogen-row-${r.domain}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-[#1a1a1a]">{r.name}</div>
                        <div className="text-xs text-gray-400">{r.total_reviews} avis</div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => updateRow(r.domain, { enabled: !r.enabled })}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${r.enabled ? 'bg-[#00b67a]' : 'bg-gray-300'}`}
                          data-testid={`autogen-toggle-${r.domain}`}
                          role="switch"
                          aria-checked={r.enabled}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${r.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          max="140"
                          value={r.weekly_volume}
                          disabled={!r.enabled}
                          onChange={(e) => updateRow(r.domain, { weekly_volume: e.target.value })}
                          className="w-20 border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#00b67a] disabled:bg-gray-100"
                          data-testid={`autogen-volume-${r.domain}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={r.rating_preset}
                          disabled={!r.enabled}
                          onChange={(e) => updateRow(r.domain, { rating_preset: e.target.value })}
                          className="border rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#00b67a] disabled:bg-gray-100"
                          data-testid={`autogen-preset-${r.domain}`}
                        >
                          {RATING_PRESETS.map((p) => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 mt-6">
          {saved && <span className="text-sm text-[#00b67a] font-medium" data-testid="autogen-saved">Réglages enregistrés ✓</span>}
          <button onClick={load} className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#00b67a] transition-colors">
            <RefreshCw className="w-4 h-4" /> Recharger
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] disabled:opacity-50 transition-colors font-medium"
            data-testid="autogen-save"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoGenSettings;
